import { stripMentionMarkers } from '@design/chat/mention-composer/markers';
import fontsCss from '@static/fonts/fonts.css?raw';
import baseCss from '@static/styles/base.css?raw';
import resetCss from '@static/styles/reset.css?raw';
import variablesCss from '@static/styles/variables.css?raw';
import { config } from '@terminal/config';
import {
  getBrandedPdfContent,
  PDF_WATERMARK_PRINT_CSS,
} from '@terminal/utils/pdf-branding/pdf-branding';
import { extractStylesheetRules } from '@terminal/views/insight-feed/utils/fetchStylesUtils';
import type { ConversationInventory } from '../services/inventory-service';
import { ConversationTask } from '../use-chat/use-chat.types';
import { VisualizationTable } from '../use-visualization-store/useVisualizationStore';
import { buildInventoryContent } from './buildInventoryContent';
import { prepareChartFonts } from './copyAlfredCharts';
import { BODY_STYLE, htmlNodes } from './htmlNodes';
import { markdownParser } from './markdownParser';
import { patterns } from './regex';

export type CopyAction = 'copy' | 'print';

interface CopyResearchMessageArgs {
  task: ConversationTask;
  charts: Record<string, HTMLDivElement>;
  tables: Record<string, VisualizationTable>;
  inventory?: ConversationInventory;
  action: CopyAction;
  taskQuestion: string;
}

const stripCitationLinks = (content: string): string =>
  content.replace(patterns.citationLink, (_, text) => text);

export const copyResearchMessage = async ({
  task,
  charts,
  tables,
  inventory,
  action,
  taskQuestion,
}: CopyResearchMessageArgs) => {
  if (task.states) {
    await prepareChartFonts();

    let htmlContent = '';
    let plainContent = '';

    for (const curr of task.states) {
      const title = stripMentionMarkers(curr.title);
      const content = stripMentionMarkers(curr.content);

      plainContent += `\n\n## ${title}\n\n`;
      htmlContent += htmlNodes.h2(title);

      const processedHtmlContent = await markdownParser({
        content,
        charts,
        tables,
      });

      plainContent += stripCitationLinks(content);
      htmlContent += processedHtmlContent + '\n';
    }

    const { html: inventoryHtml, text: inventoryText } =
      await buildInventoryContent({ inventory });

    plainContent += inventoryText;
    htmlContent += inventoryHtml;

    if (action === 'print') {
      htmlContent = getBrandedPdfContent(config.urls.root, htmlContent);
    }

    const browserStylesheetStyles = (
      await Promise.all(
        Array.from(document.styleSheets).map(stylesheet =>
          extractStylesheetRules(stylesheet)
        )
      )
    )
      .filter(Boolean)
      .join('\n');

    const printDocument = `
  <!DOCTYPE html>
      <html class="light" data-theme="light_default">
      <head>
        <meta charset="UTF-8">
        <title>${taskQuestion}</title>
        <style>
          body { ${BODY_STYLE} }

          /* Static CSS files */
          ${resetCss}
          ${variablesCss}
          ${baseCss}
          ${fontsCss}

          /* Stylesheets extracted from the browser's stylesheet API */
          ${browserStylesheetStyles}

          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            ${PDF_WATERMARK_PRINT_CSS}
          }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
      </html>
  `;

    if (action === 'print') {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      const iframeDoc =
        iframe.contentDocument || iframe.contentWindow?.document;

      if (iframeDoc) {
        const blob = new Blob([printDocument], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        iframe.src = url;

        iframe.onload = async () => {
          const originalTitle = document.title;
          document.title = `${taskQuestion}`;

          await iframeDoc.fonts?.ready;

          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();

          setTimeout(() => {
            URL.revokeObjectURL(url);
            document.title = originalTitle;
            document.body.removeChild(iframe);
          }, 100);
        };
      }
    } else {
      const clipboardData: Record<string, Blob> = {
        'text/plain': new Blob([plainContent.trim()], { type: 'text/plain' }),
        'text/html': new Blob([printDocument], { type: 'text/html' }),
      };

      await navigator.clipboard.write([new ClipboardItem(clipboardData)]);
    }
  }
};
